import type { TimesheetGridStickyHeaderProps } from '../sticky-header/TimesheetGridStickyHeader';
import type { TimesheetGridTableProps } from '../table/TimesheetGridTable';
import type { TimesheetGridDialogsProps } from '../TimesheetGridDialogs';
import type { TimesheetSubmitFooterProps } from '../submit-footer/TimesheetSubmitFooter';
import { useTimesheetGridCore, type TimesheetDrilldownState } from './useTimesheetGridCore';
import { useTimesheetGridActions } from './useTimesheetGridActions';
import type { SelectorPreferencesOwner } from '@/hooks/use-selector-preferences';

export interface UseTimesheetGridParams {
  navigationState?: TimesheetDrilldownState;
}

export interface UseTimesheetGridResult {
  isReadOnly: boolean;
  addRow: () => void;
  /** Whose project / cost centre favourites and recents the grid's selectors show. */
  selectorPreferencesOwner: SelectorPreferencesOwner | null;
  stickyHeaderProps: TimesheetGridStickyHeaderProps;
  tableProps: Omit<TimesheetGridTableProps, 'tableScrollRef'> & { tableScrollRef: React.RefObject<HTMLDivElement | null> };
  submitFooterProps: TimesheetSubmitFooterProps;
  dialogProps: TimesheetGridDialogsProps;
}

/**
 * Orchestrates every piece of timesheet grid state and returns the props for each
 * section of the grid, so `TimesheetGrid` stays a thin composition shell.
 */
export function useTimesheetGrid({ navigationState }: UseTimesheetGridParams): UseTimesheetGridResult {
  const core = useTimesheetGridCore({ navigationState });
  const {
    entries, currentTimesheet, currentEmployee, organizationSettings,
    permissions, screenMode, isReadOnly, isDaily, visibleDays, data, overtime, validation,
  } = core;
  const actions = useTimesheetGridActions(core);
  const {
    rowActions, bulkTools, classification, submission, resubmit, weekNavigation,
    editingHours, openNoteCell, draftNote, showAuditHistory, showAIInsightsSheet,
    selectedBookingForDetails, selectedBookingForDismiss, selectedAbsenceForDetails,
  } = actions;

  // Two-tier gate: org setting (master switch) AND role permission must both be true.
  // Org OFF → hidden for everyone. Org ON → role permission decides per user.
  const enableCopyPreviousWeek = (organizationSettings?.deadline?.enableCopyPreviousWeek ?? true) && permissions.canCopyFromLastWeek();
  const enableAutoSmartFill = (organizationSettings?.deadline?.enableAutoSmartFill ?? true) && permissions.canUseSmartFill();
  const enableAiInsights = (organizationSettings?.deadline?.enableAiInsights ?? true) && permissions.canUseAiInsights();

  return {
    isReadOnly,
    addRow: rowActions.addRow,
    selectorPreferencesOwner: core.selectorPreferencesOwner,
    stickyHeaderProps: {
      timesheetId: currentTimesheet.id,
      onOpenAuditHistory: () => actions.setShowAuditHistory(true),
      statusBanners: {
        isViewingOtherResource: core.isViewingOtherResource,
        viewedResourceName: core.viewedResourceName,
        canEditThisTimesheet: core.canEditThisTimesheet,
      },
      weekNav: {
        currentWeekStart: core.currentWeekStart,
        weekStartDay: core.weekStartDay,
        status: currentTimesheet.status,
        organizationSettings,
        isViewingOtherResource: core.isViewingOtherResource,
        isRejected: !!core.isRejected,
        pendingRejection: core.pendingRejection,
        onNavigate: weekNavigation.navigateWeek,
        onNavigateToDate: weekNavigation.navigateToDate,
      },
    },
    tableProps: {
      entries,
      rowTotals: core.totals.rowTotals,
      gridColumnCount: core.gridColumnCount,
      tableScrollRef: core.tableScrollRef,
      header: {
        visibleDays,
        currentWeekStart: core.currentWeekStart,
        weekStartDay: core.weekStartDay,
        nonWorkingDays: core.nonWorkingDays,
        columnWidths: core.columnResize.columnWidths,
        handleResizeStart: core.columnResize.handleResizeStart,
        registerColumnHeader: core.columnResize.registerColumnHeader,
        headerRowRef: core.columnResize.headerRowRef,
        spacerHeaderRef: core.columnResize.spacerHeaderRef,
        organizationSettings,
        overtimeEnabled: overtime.overtimeEnabled,
        isDaily,
        isReadOnly,
      },
      rows: {
        allAvailableProjects: data.allAvailableProjects,
        costCentres: core.costCentres,
        costCentresByProjectCode: data.costCentresByProjectCode,
        visibleDays,
        currentWeekStart: core.currentWeekStart,
        weekStartDay: core.weekStartDay,
        nonWorkingDays: core.nonWorkingDays,
        editingHours,
        openNoteCell,
        draftNote,
        isReadOnly,
        isDaily,
        isSearchingProjects: data.isSearchingProjects,
        companyId: core.currentCompany?.id,
        employeeId: currentTimesheet.employeeId ?? currentEmployee?.id,
        screenMode,
        overtimeEnabled: overtime.overtimeEnabled,
        activeOvertimeTypes: overtime.activeOvertimeTypes,
        organizationSettings,
        canDuplicate: permissions.canDuplicateTimesheet(),
        canDelete: permissions.canDeleteTimesheet(),
        onUpdateEntry: rowActions.updateEntry,
        onUpdateHours: rowActions.updateHours,
        onProjectSearch: data.handleProjectSearch,
        onFetchCostCentres: data.fetchCostCentresForProject,
        onCostCentreUsed: core.updateCostCentreLastUsed,
        onSetEditingHours: actions.setEditingHours,
        onSetOpenNoteCell: actions.setOpenNoteCell,
        onSetDraftNote: actions.setDraftNote,
        onDuplicate: rowActions.duplicateRow,
        onDelete: rowActions.deleteRow,
        onViewBookingDetails: actions.setSelectedBookingForDetails,
        onViewAbsenceDetails: actions.setSelectedAbsenceForDetails,
        onClassifyOvertime: classification.setClassifyOvertimeEntry,
      },
      footer: {
        isReadOnly,
        enableCopyPreviousWeek,
        enableAutoSmartFill,
        onAddRow: rowActions.addRow,
        onCopyLastWeek: bulkTools.copyFromLastWeek,
        onOpenAutoFill: () => bulkTools.setShowAutoFillDialog(true),
        visibleDays,
        columnTotals: core.totals.columnTotals,
        weekTotal: core.totals.weekTotal,
        daysExceedingThreshold: overtime.daysExceedingThreshold,
        weekExceedsThreshold: overtime.weekExceedsThreshold,
        nonWorkingDays: core.nonWorkingDays,
        organizationSettings,
        overtimeEnabled: overtime.overtimeEnabled,
        isDaily,
      },
    },
    submitFooterProps: {
      isDaily,
      isSubmitted: core.isSubmitted,
      isReadOnly,
      isRejected: !!core.isRejected,
      isSaving: submission.isSaving,
      isSubmitting: submission.isSubmitting,
      isDirty: actions.isDirty,
      lastSaved: core.lastSaved,
      entriesCount: entries.length,
      hasMissingProjects: validation.hasMissingProjects,
      hasMissingDescriptions: validation.hasMissingDescriptions,
      hasMissingCostCentres: validation.hasMissingCostCentres,
      hasMissingRoles: validation.hasMissingRoles,
      hasBlockingPolicyViolation: core.aiWarningsState.hasBlockingPolicyViolation,
      currentTimesheet,
      canRecallTimesheet: permissions.canRecallTimesheet(),
      enableAiInsights,
      aiWarningsCount: core.aiWarningsState.aiWarnings.length,
      onOpenAiInsights: () => actions.setShowAIInsightsSheet(true),
      onSave: submission.saveTimesheet,
      onSubmit: submission.submitTimesheet,
      onResubmit: resubmit.handleResubmit,
      onRecall: resubmit.handleRecallTimesheet,
    },
    dialogProps: {
      isDaily,
      showFillDescriptionDialog: bulkTools.showFillDescriptionDialog,
      setShowFillDescriptionDialog: bulkTools.setShowFillDescriptionDialog,
      emptyDescriptionCount: validation.emptyDescriptionCount,
      bulkDescription: bulkTools.bulkDescription,
      setBulkDescription: bulkTools.setBulkDescription,
      handleFillEmptyDescriptions: bulkTools.handleFillEmptyDescriptions,
      selectedBookingForDetails,
      setSelectedBookingForDetails: actions.setSelectedBookingForDetails,
      selectedBookingForDismiss,
      setSelectedBookingForDismiss: actions.setSelectedBookingForDismiss,
      handleDismissBooking: rowActions.handleDismissBooking,
      selectedAbsenceForDetails,
      setSelectedAbsenceForDetails: actions.setSelectedAbsenceForDetails,
      showAIInsightsSheet,
      setShowAIInsightsSheet: actions.setShowAIInsightsSheet,
      aiWarnings: core.aiWarningsState.aiWarnings,
      handleDismissWarning: core.aiWarningsState.handleDismissWarning,
      handleAIClassifyOvertime: classification.handleAIClassifyOvertime,
      nonDismissableWarningIds: core.aiWarningsState.nonDismissableWarningIds,
      showAutoFillDialog: bulkTools.showAutoFillDialog,
      setShowAutoFillDialog: bulkTools.setShowAutoFillDialog,
      autoFillSuggestions: bulkTools.autoFillSuggestions,
      projects: core.projects,
      handleApplyAutoFill: bulkTools.handleApplyAutoFill,
      classifyOvertimeEntry: classification.classifyOvertimeEntry,
      setClassifyOvertimeEntry: classification.setClassifyOvertimeEntry,
      classifyOvertimeSuggestedHours: classification.classifyOvertimeSuggestedHours,
      setClassifyOvertimeSuggestedHours: classification.setClassifyOvertimeSuggestedHours,
      visibleDays,
      dayLabels: core.dayLabels,
      activeOvertimeTypes: overtime.activeOvertimeTypes,
      defaultOvertimeTypeId: overtime.defaultOvertimeTypeId,
      handleClassifyOvertimeConfirm: classification.handleClassifyOvertimeConfirm,
      handleProjectSearch: data.handleProjectSearch,
      isSearchingProjects: data.isSearchingProjects,
      showCopyLastWeekDialog: bulkTools.showCopyLastWeekDialog,
      setShowCopyLastWeekDialog: bulkTools.setShowCopyLastWeekDialog,
      copyLastWeekSourceEntries: bulkTools.copyLastWeekSourceEntries,
      handleCopyLastWeekConfirm: bulkTools.handleCopyLastWeekConfirm,
      confirmationDialogOpen: submission.confirmationDialogOpen,
      confirmationMessage: submission.confirmationMessage,
      isSubmitting: submission.isSubmitting,
      handleConfirmationAccept: submission.handleConfirmationAccept,
      handleConfirmationDecline: submission.handleConfirmationDecline,
      deleteConfirmationOpen: rowActions.deleteConfirmation.open,
      onDeleteConfirmationChange: (open: boolean) => rowActions.setDeleteConfirmation({ open, index: null }),
      handleConfirmDelete: rowActions.confirmDelete,
      showAuditHistory,
      setShowAuditHistory: actions.setShowAuditHistory,
      timesheetId: currentTimesheet.id,
      employeeName: currentEmployee.name,
      weekEnding: currentTimesheet.weekEnding,
      timesheet: currentTimesheet,
      allAvailableProjects: data.allAvailableProjects,
      costCentres: core.costCentres,
      entries,
      isReadOnly,
      overtimeEnabled: overtime.overtimeEnabled,
      organizationSettings,
      companyId: core.currentCompany?.id,
    },
  };
}
